/**
 * Presentation model for the admin coach directory: column and view
 * definitions, status and facet labels, summary segments, formatting, and the
 * CSV shape. Pure — the JSX cells live in `coach-directory-cells.tsx`.
 *
 * Aliases: coach directory columns, coach directory views, column presets,
 * coach csv export, coach status labels, platform use filter
 */

import { MembershipPlan } from "~/lib/schemas/db-enums";
import {
  ACCOUNT_STATUSES,
  type AccountStatus,
  ACTIVITY_FILTERS,
  type ActivityFilter,
  COACH_SOURCES,
  COACH_TAGS,
  COACH_USE_FILTERS,
  type CoachDirectoryFacet,
  type CoachDirectoryFilters,
  type CoachDirectoryRow,
  type CoachDirectorySort,
  type CoachDirectorySummary,
  type CoachSource,
  type CoachTag,
  type CoachUseFilter,
  LISTING_STATUSES,
  type ListingStatus,
  NOVA_STATES,
  type NovaState,
} from "~/lib/shared/admin-coach-directory";
import { COACH_TIER_LABELS, tierForPlanKey } from "~/lib/shared/coach-account";
import { toCsv } from "~/lib/shared/csv";
import { PLAN_LABELS } from "~/lib/shared/membership-plan";
import { hasPermission, type Permission } from "~/lib/shared/permissions";

const DAY_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  approved: "Approved",
  pending: "Awaiting approval",
  revoked: "Revoked",
};

export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  listed: "Listed",
  hidden: "Approved, private",
  inReview: "In review",
  returned: "Returned",
  revoked: "Removed",
  notSubmitted: "Not submitted",
};

export const NOVA_STATE_LABELS: Record<NovaState, string> = {
  paying: "Paying",
  trialing: "Trial",
  canceling: "Canceling",
  billingIssue: "Payment issue",
  freeAccess: "Free access",
  lapsed: "Lapsed",
  never: "No Nova",
};

export const SOURCE_LABELS: Record<CoachSource, string> = {
  organic: "Signed up",
  aiProfile: "AI profile",
};

export const ACTIVITY_LABELS: Record<ActivityFilter, string> = {
  active30d: "Active in the last 30 days",
  quiet30d: "Quiet for 30+ days",
  neverActive: "Never active",
};

export const TAG_LABELS: Record<CoachTag, string> = {
  founding: "Founding coach",
  featured: "Featured coach",
  ghl: "GHL sub-account",
  payouts: "Stripe payouts on",
  feeExempt: "Fee exempt",
  noLogin: "No login",
  acceleratorGraduate: "Accelerator graduate",
};

export const USE_LABELS: Record<CoachUseFilter, string> = {
  coachingOs: "Has worked in the Coaching OS",
  neverCoachingOs: "Never worked in the Coaching OS",
  lifeDesign: "Has their own Life by Design",
  cihAuthor: "Has written a Hub article",
  unanswered: "Has unanswered inquiries",
};

type CoachFlag = keyof CoachDirectoryRow["flags"];

export const FLAGS: Record<CoachFlag, { label: string; description: string }> =
  {
    founding: { label: "Founding", description: "Founding coach" },
    featured: { label: "Featured", description: "Featured coach" },
    ghl: {
      label: "GHL",
      description:
        "Has a GoHighLevel sub-account (Marketing Suite or legacy GHL)",
    },
    payouts: {
      label: "Payouts",
      description: "Stripe Connect payouts enabled",
    },
    contractSigned: {
      label: "Contract",
      description: "Coaching contract signed",
    },
    feeExempt: {
      label: "Fee exempt",
      description: "Platform fee exemption active now",
    },
    joinedCommunity: {
      label: "Community",
      description: "Joined the coach community",
    },
  };

const FLAG_ORDER: readonly CoachFlag[] = [
  "founding",
  "featured",
  "ghl",
  "payouts",
  "contractSigned",
  "feeExempt",
  "joinedCommunity",
];

export function activeFlags(flags: CoachDirectoryRow["flags"]): CoachFlag[] {
  return FLAG_ORDER.filter((flag) => flags[flag]);
}

// ---------------------------------------------------------------------------
// Facets and summary segments
// ---------------------------------------------------------------------------

export type FacetFilters = Pick<CoachDirectoryFilters, CoachDirectoryFacet>;

export const FACET_KEYS = [
  "account",
  "listing",
  "nova",
  "activity",
  "use",
  "source",
  "tag",
] as const satisfies readonly CoachDirectoryFacet[];

/**
 * Coaches pass two separate approvals. These explain each one wherever its
 * status is shown, since "Approval" and "Listing" read as synonyms otherwise.
 */
const APPROVAL_HINT =
  "Tier 1: whether this person may use the platform as a coach at all";
const LISTING_HINT =
  "Tier 2: whether the coach's profile appears in the public marketplace. Listed means approved and public; Approved, private means approved but the profile is set to private";

export const FACETS: {
  readonly [Facet in CoachDirectoryFacet]: {
    readonly label: string;
    /** Explains a facet whose meaning is not obvious from its label. */
    readonly hint?: string;
    readonly options: Readonly<
      Record<NonNullable<CoachDirectoryFilters[Facet]>, string>
    >;
  };
} = {
  account: {
    label: "Approval",
    hint: APPROVAL_HINT,
    options: ACCOUNT_STATUS_LABELS,
  },
  listing: {
    label: "Listing",
    hint: LISTING_HINT,
    options: LISTING_STATUS_LABELS,
  },
  nova: { label: "Nova", options: NOVA_STATE_LABELS },
  activity: { label: "Activity", options: ACTIVITY_LABELS },
  use: { label: "Platform use", options: USE_LABELS },
  source: { label: "Source", options: SOURCE_LABELS },
  tag: { label: "Tag", options: TAG_LABELS },
};

/** Sets or clears one facet, keeping only a value that facet accepts. */
export function withFacet(
  facets: FacetFilters,
  facet: CoachDirectoryFacet,
  value: string | undefined,
): FacetFilters {
  switch (facet) {
    case "account":
      return {
        ...facets,
        account: ACCOUNT_STATUSES.find((option) => option === value),
      };
    case "listing":
      return {
        ...facets,
        listing: LISTING_STATUSES.find((option) => option === value),
      };
    case "nova":
      return {
        ...facets,
        nova: NOVA_STATES.find((option) => option === value),
      };
    case "source":
      return {
        ...facets,
        source: COACH_SOURCES.find((option) => option === value),
      };
    case "activity":
      return {
        ...facets,
        activity: ACTIVITY_FILTERS.find((option) => option === value),
      };
    case "tag":
      return { ...facets, tag: COACH_TAGS.find((option) => option === value) };
    case "use":
      return {
        ...facets,
        use: COACH_USE_FILTERS.find((option) => option === value),
      };
  }
}

export interface SummarySegment {
  readonly key: keyof CoachDirectorySummary;
  readonly label: string;
  /** Clicking the segment replaces every facet filter with exactly this. */
  readonly filters: FacetFilters;
}

export const SUMMARY_GROUPS: readonly {
  readonly label: string | null;
  readonly segments: readonly SummarySegment[];
}[] = [
  {
    label: null,
    segments: [{ key: "total", label: "All coaches", filters: {} }],
  },
  {
    label: "Marketplace",
    segments: [
      { key: "listed", label: "Listed", filters: { listing: "listed" } },
      { key: "inReview", label: "In review", filters: { listing: "inReview" } },
    ],
  },
  {
    label: "Nova",
    segments: [
      { key: "novaPaying", label: "Paying", filters: { nova: "paying" } },
      { key: "novaTrialing", label: "Trial", filters: { nova: "trialing" } },
      {
        key: "novaCanceling",
        label: "Canceling",
        filters: { nova: "canceling" },
      },
      {
        key: "novaBillingIssue",
        label: "Payment issue",
        filters: { nova: "billingIssue" },
      },
    ],
  },
  {
    label: "Activity",
    segments: [
      {
        key: "active30d",
        label: "Active 30d",
        filters: { activity: "active30d" },
      },
      {
        key: "unanswered",
        label: "Not replied",
        filters: { use: "unanswered" },
      },
    ],
  },
];

export function isSegmentActive(
  segment: SummarySegment,
  current: FacetFilters,
): boolean {
  return FACET_KEYS.every((key) => segment.filters[key] === current[key]);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatDate(iso: string): string {
  return dateFormatter.format(new Date(iso));
}

/** Compact enough for a table cell: "Today", "3d ago", "5w ago", "2y ago". */
export function formatRelative(iso: string, now: Date): string {
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS);
  if (days < 1) return "Today";
  if (days < 2) return "Yesterday";
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function formatUsd(amount: number): string {
  return usdFormatter.format(amount);
}

export function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function novaPeriodLabel(
  nova: CoachDirectoryRow["nova"],
): string | null {
  if (nova.periodEnd === null) return null;
  const date = formatDate(nova.periodEnd);

  switch (nova.state) {
    case "paying":
      return `Renews ${date}`;
    case "trialing":
      return `Trial ends ${date}`;
    case "canceling":
      return `Ends ${date}`;
    case "billingIssue":
      return `Period ends ${date}`;
    case "freeAccess":
    case "lapsed":
    case "never":
      return null;
  }
}

/**
 * What the coach pays for, by product name: her NOVA plan while she holds one
 * (trial, paying, ending or in dunning), and the Marketing Suite when she has
 * it outside NOVA. Null when she holds neither.
 */
export function planLabel(
  row: Pick<CoachDirectoryRow, "nova" | "membership">,
): string | null {
  const holdsNova = row.nova.state !== "lapsed" && row.nova.state !== "never";
  // Free access is NOVA Core whatever plan key an ended subscription left.
  const tier =
    row.nova.state === "freeAccess"
      ? "core"
      : holdsNova
        ? tierForPlanKey(row.nova.planKey)
        : null;
  // `membership.plan` is what she holds outside NOVA; NOVA Pro and Pro+
  // already contain the Suite.
  const suite = row.membership.plan !== MembershipPlan.Standard;
  if (tier === null) {
    if (holdsNova) return "NOVA (plan not recorded)";
    return suite ? "Marketing Suite only" : null;
  }
  return tier === "core" && suite
    ? `${COACH_TIER_LABELS[tier]} + Marketing Suite`
    : COACH_TIER_LABELS[tier];
}

export function membershipLabel(
  membership: CoachDirectoryRow["membership"],
): string {
  const plan = PLAN_LABELS[membership.plan];
  const parts = [
    plan,
    membership.lifetime ? "lifetime" : null,
    membership.status?.replaceAll("_", " ") ?? null,
  ];
  return parts.filter((part) => part !== null).join(" · ");
}

/** A calendar day stored at UTC midnight. Read locally, it shows the day before west of UTC. */
const calendarDayFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

export function formatCalendarDay(iso: string): string {
  return calendarDayFormatter.format(new Date(iso));
}

/** Her Accelerator standing in one line, or null when she has none. */
export function acceleratorLabel(
  accelerator: CoachDirectoryRow["accelerator"],
): string | null {
  const parts = [
    accelerator.cohort === null ? null : `Cohort ${accelerator.cohort}`,
    accelerator.graduatedAt === null
      ? null
      : `graduated ${formatCalendarDay(accelerator.graduatedAt)}`,
    accelerator.purchasedAt === null
      ? null
      : `bought ${formatDate(accelerator.purchasedAt)}`,
  ].filter((part) => part !== null);
  if (parts.length === 0) return null;
  const line = parts.join(" · ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

const HTTP_SCHEME = /^https?:\/\//i;
/** Any other scheme (`javascript:`, `mailto:`), but not a bare `host:port`. */
const OTHER_SCHEME = /^[a-z][a-z\d+.-]*:(?!\d)/i;

/**
 * A coach-written link as an openable web URL and a short label, or null when
 * it is not an http(s) address. Coaches often leave the scheme off.
 */
export function webLink(
  value: string | null,
): { href: string; label: string } | null {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "") return null;
  if (!HTTP_SCHEME.test(trimmed) && OTHER_SCHEME.test(trimmed)) return null;

  let url: URL;
  try {
    url = new URL(HTTP_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const path = url.pathname.replace(/\/+$/, "");
  return {
    href: url.href,
    label: `${url.hostname.replace(/^www\./, "")}${path}`,
  };
}

export function stripeCustomerUrl(customerId: string): string {
  return `https://dashboard.stripe.com/customers/${encodeURIComponent(customerId)}`;
}

export function stripeSubscriptionUrl(subscriptionId: string): string {
  return `https://dashboard.stripe.com/subscriptions/${encodeURIComponent(subscriptionId)}`;
}

export function locationLabel(row: CoachDirectoryRow): string | null {
  const parts = [row.city, row.country].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length === 0 ? null : parts.join(", ");
}

// ---------------------------------------------------------------------------
// Columns and views
// ---------------------------------------------------------------------------

export const READINESS_COLUMN_IDS = [
  "readinessScore",
  "readinessComponents",
  "readinessHoldingBack",
  "readinessRevenue",
  "readinessTaken",
] as const;
export type ReadinessColumnId = (typeof READINESS_COLUMN_IDS)[number];

export const COACH_DIRECTORY_COLUMN_IDS = [
  "joined",
  "source",
  "account",
  "listing",
  "quality",
  "onboarding",
  "flags",
  "nova",
  "plan",
  "novaPeriod",
  "membership",
  "referralCredit",
  "novaLastUsed",
  "novaLastDay",
  "novaThreads",
  "novaRuns",
  "novaDeliverables",
  "novaCost30d",
  "lastActive",
  "clients",
  "sessions30d",
  "messages30d",
  "inquiries",
  "inquiries30d",
  "leads",
  "leads30d",
  "unanswered",
  "messagesReceived30d",
  "coachingOs",
  "lifeDesign",
  "articles",
  "phone",
  "location",
  "website",
  "linkedin",
  "instagram",
  ...READINESS_COLUMN_IDS,
] as const;
export type CoachDirectoryColumnId =
  (typeof COACH_DIRECTORY_COLUMN_IDS)[number];

export const COLUMN_GROUPS = [
  "Status",
  "Billing",
  "Nova",
  "Activity",
  "Inbox",
  "Platform",
  "Contact",
  "Readiness",
] as const;

export interface ColumnDefinition {
  readonly label: string;
  readonly group: (typeof COLUMN_GROUPS)[number];
  readonly sort?: CoachDirectorySort;
  /** Right-aligned, tabular figures. */
  readonly numeric?: boolean;
  /** Explains a figure whose definition is not obvious from its label. */
  readonly hint?: string;
  /** `null` keeps the column out of every export. */
  readonly csv: ((row: CoachDirectoryRow) => string) | null;
  /** Staff without it never see the column, and the server withholds its data. */
  readonly permission?: Permission;
}

/** Readiness results are not on the directory row, so no export carries them. */
const READINESS_COLUMN = {
  group: "Readiness",
  csv: null,
  permission: "coach-assessments:read",
} as const satisfies Partial<ColumnDefinition>;

const day = (iso: string | null): string => iso?.slice(0, 10) ?? "";

export const COLUMNS: Record<CoachDirectoryColumnId, ColumnDefinition> = {
  joined: {
    label: "Joined",
    group: "Status",
    sort: "joined",
    csv: (row) => day(row.joinedAt),
  },
  source: {
    label: "Source",
    group: "Status",
    csv: (row) => SOURCE_LABELS[row.source],
  },
  account: {
    label: "Approval",
    group: "Status",
    hint: APPROVAL_HINT,
    csv: (row) => ACCOUNT_STATUS_LABELS[row.accountStatus],
  },
  listing: {
    label: "Listing",
    group: "Status",
    hint: LISTING_HINT,
    csv: (row) => LISTING_STATUS_LABELS[row.listingStatus],
  },
  quality: {
    label: "Profile quality",
    group: "Status",
    sort: "quality",
    hint: "The completeness score the marketplace ranks coaches by",
    csv: (row) => formatPercent(row.qualityScore),
  },
  onboarding: {
    label: "Onboarding",
    group: "Status",
    csv: (row) => day(row.onboardingCompletedAt),
  },
  flags: {
    label: "Flags",
    group: "Status",
    csv: (row) =>
      activeFlags(row.flags)
        .map((flag) => FLAGS[flag].label)
        .join("; "),
  },
  nova: {
    label: "Nova",
    group: "Billing",
    csv: (row) => NOVA_STATE_LABELS[row.nova.state],
  },
  plan: {
    label: "Plan",
    group: "Billing",
    hint: "The NOVA plan the coach holds, or the Marketing Suite on its own",
    csv: (row) => planLabel(row) ?? "",
  },
  novaPeriod: {
    label: "Nova period",
    group: "Billing",
    sort: "novaRenewal",
    hint: "When the current Nova billing period or trial ends",
    csv: (row) => day(row.nova.periodEnd),
  },
  membership: {
    label: "Membership",
    group: "Billing",
    csv: (row) => membershipLabel(row.membership),
  },
  referralCredit: {
    label: "Referral credit",
    group: "Billing",
    numeric: true,
    hint: "Granted referral rewards",
    csv: (row) => (row.referralCreditCents / 100).toFixed(2),
  },
  novaLastUsed: {
    label: "Nova last used",
    group: "Nova",
    sort: "novaLastUsed",
    hint: "Latest message the coach wrote to Nova, or run they started",
    csv: (row) => day(row.nova.lastUsedAt),
  },
  novaLastDay: {
    label: "Last NOVA day",
    group: "Nova",
    hint: "Latest day (UTC) the coach opened a NOVA page",
    csv: (row) => row.nova.lastDay ?? "",
  },
  novaThreads: {
    label: "Threads",
    group: "Nova",
    numeric: true,
    csv: (row) => String(row.nova.threads),
  },
  novaRuns: {
    label: "Runs",
    group: "Nova",
    numeric: true,
    csv: (row) => String(row.nova.runs),
  },
  novaDeliverables: {
    label: "Deliverables",
    group: "Nova",
    numeric: true,
    csv: (row) => String(row.nova.deliverables),
  },
  novaCost30d: {
    label: "Nova cost 30d",
    group: "Nova",
    sort: "novaCost30d",
    numeric: true,
    hint: "Estimated model cost over the last 30 days",
    csv: (row) => row.nova.cost30dUsd.toFixed(2),
  },
  lastActive: {
    label: "Last active",
    group: "Activity",
    sort: "lastActive",
    hint: "Latest Nova use, message sent, client added, or session held",
    csv: (row) => day(row.activity.lastActiveAt),
  },
  clients: {
    label: "Active clients",
    group: "Activity",
    sort: "activeClients",
    numeric: true,
    csv: (row) => String(row.activity.activeClients),
  },
  sessions30d: {
    label: "Sessions 30d",
    group: "Activity",
    numeric: true,
    hint: "Sessions held in the last 30 days, not counting cancellations",
    csv: (row) => String(row.activity.sessions30d),
  },
  messages30d: {
    label: "Messages 30d",
    group: "Activity",
    numeric: true,
    hint: "Messages the coach sent in the last 30 days",
    csv: (row) => String(row.activity.messagesSent30d),
  },
  inquiries: {
    label: "Inquiries",
    group: "Inbox",
    sort: "inquiries",
    numeric: true,
    hint: "Conversations someone else started. The last 30 days shows beside it and exports as its own column",
    csv: (row) => String(row.inbox.inquiries),
  },
  inquiries30d: {
    label: "New inquiries 30d",
    group: "Inbox",
    numeric: true,
    hint: "Inquiries opened in the last 30 days — the figure beside the Inquiries total",
    csv: (row) => String(row.inbox.inquiries30d),
  },
  leads: {
    label: "Leads",
    group: "Inbox",
    sort: "leads",
    numeric: true,
    hint: "People who shared their details on one of the coach's lead magnets, as in her Lead magnets > Leads list. An inquiry is a conversation somebody else opened. The last 30 days shows beside it and exports as its own column",
    csv: (row) => String(row.inbox.leads),
  },
  leads30d: {
    label: "New leads 30d",
    group: "Inbox",
    numeric: true,
    hint: "Leads who shared their details in the last 30 days — the figure beside the Leads total",
    csv: (row) => String(row.inbox.leads30d),
  },
  unanswered: {
    label: "Not replied",
    group: "Inbox",
    sort: "unanswered",
    numeric: true,
    hint: "Inquiries with no reply from the coach anywhere in the thread",
    csv: (row) => String(row.inbox.unanswered),
  },
  messagesReceived30d: {
    label: "Received 30d",
    group: "Inbox",
    numeric: true,
    hint: "Messages written to the coach in the last 30 days",
    csv: (row) => String(row.inbox.messagesReceived30d),
  },
  coachingOs: {
    label: "Coaching OS",
    group: "Platform",
    hint: "Latest client added, session held, or message sent. Nova use does not count, and we record no page views",
    csv: (row) => day(row.platform.coachingOsLastUsedAt),
  },
  lifeDesign: {
    label: "Life by Design",
    group: "Platform",
    hint: "Entries in the coach's own Life by Design: categories, goals, vision, and bucket list",
    csv: (row) => String(row.platform.lifeDesignItems),
  },
  articles: {
    label: "Hub articles",
    group: "Platform",
    sort: "articles",
    numeric: true,
    hint: "Coaching Intelligence Hub articles, published ones first",
    csv: (row) =>
      `${row.platform.articlesPublished} of ${row.platform.articlesTotal}`,
  },
  phone: {
    label: "Phone",
    group: "Contact",
    csv: (row) => row.phone ?? "",
  },
  location: {
    label: "Location",
    group: "Contact",
    csv: (row) => locationLabel(row) ?? "",
  },
  website: {
    label: "Website",
    group: "Contact",
    csv: (row) => row.website ?? "",
  },
  linkedin: {
    label: "LinkedIn",
    group: "Contact",
    csv: (row) => row.linkedinUrl ?? "",
  },
  instagram: {
    label: "Instagram",
    group: "Contact",
    csv: (row) => row.instagramUrl ?? "",
  },
  readinessScore: {
    ...READINESS_COLUMN,
    label: "Readiness",
    sort: "readiness",
    hint: "The score from the coach's latest saved 6-Figure Readiness result, and its band",
  },
  readinessComponents: {
    ...READINESS_COLUMN,
    label: "Components",
    hint: "The six components in course order: positioning, offer, social presence, lead generation, conversion mechanism, proof",
  },
  readinessHoldingBack: {
    ...READINESS_COLUMN,
    label: "Holding back",
    hint: "The component the result says to work on first",
  },
  readinessRevenue: {
    ...READINESS_COLUMN,
    label: "Revenue",
    hint: "The revenue answer in the latest result",
  },
  readinessTaken: {
    ...READINESS_COLUMN,
    label: "Taken",
    hint: "When the latest result was taken, and how many results she has saved",
  },
};

/** Whether a link asks for readiness data through its view, columns, or sort. */
export function asksForReadiness(search: {
  view?: CoachDirectoryViewId;
  cols?: readonly CoachDirectoryColumnId[];
  sort?: CoachDirectorySort;
}): boolean {
  return (
    search.view === "readiness" ||
    search.sort === "readiness" ||
    (search.cols ?? []).some(isReadinessColumn)
  );
}

/** Whether someone holding `permissions` may see this column or view. */
export function isAvailableTo(
  item: { readonly permission?: Permission },
  permissions: readonly Permission[],
): boolean {
  return (
    item.permission === undefined || hasPermission(permissions, item.permission)
  );
}

export function isReadinessColumn(
  id: CoachDirectoryColumnId,
): id is ReadinessColumnId {
  return READINESS_COLUMN_IDS.some((readinessId) => readinessId === id);
}

export const COACH_DIRECTORY_VIEW_IDS = [
  "overview",
  "onboarding",
  "billing",
  "nova",
  "activity",
  "platform",
  "contact",
  "readiness",
] as const;
export type CoachDirectoryViewId = (typeof COACH_DIRECTORY_VIEW_IDS)[number];

export const DEFAULT_VIEW: CoachDirectoryViewId = "overview";

export const VIEWS: Record<
  CoachDirectoryViewId,
  {
    readonly label: string;
    readonly description: string;
    readonly columns: readonly CoachDirectoryColumnId[];
    readonly permission?: Permission;
  }
> = {
  overview: {
    label: "Overview",
    description: "Approval, listing, Nova, and recent activity at a glance.",
    columns: ["account", "listing", "nova", "plan", "lastActive", "joined"],
  },
  onboarding: {
    label: "Onboarding",
    description: "New coaches on their way to a marketplace listing.",
    columns: [
      "joined",
      "source",
      "onboarding",
      "quality",
      "account",
      "listing",
    ],
  },
  billing: {
    label: "Billing",
    description: "Subscriptions, renewal dates, perks, and referral credit.",
    columns: [
      "nova",
      "plan",
      "novaPeriod",
      "membership",
      "flags",
      "referralCredit",
    ],
  },
  nova: {
    label: "Nova",
    description: "Who uses Nova, how much, and what it costs.",
    columns: [
      "nova",
      "novaLastUsed",
      "novaLastDay",
      "novaThreads",
      "novaRuns",
      "novaDeliverables",
      "novaCost30d",
    ],
  },
  activity: {
    label: "Activity",
    description:
      "Whether coaches work with clients, and who is writing to them.",
    columns: [
      "lastActive",
      "novaLastUsed",
      "clients",
      "leads",
      "inquiries",
      "unanswered",
      "sessions30d",
      "messages30d",
    ],
  },
  platform: {
    label: "Platform",
    description: "Which parts of what we built each coach actually uses.",
    columns: ["coachingOs", "novaLastUsed", "lifeDesign", "articles"],
  },
  contact: {
    label: "Contact",
    description: "Details for outreach lists and exports.",
    columns: [
      "phone",
      "location",
      "website",
      "linkedin",
      "instagram",
      "source",
    ],
  },
  readiness: {
    label: "Readiness",
    description:
      "Each coach's latest 6-Figure Readiness result. A coach who has not saved one shows Not taken.",
    permission: "coach-assessments:read",
    columns: [
      "readinessScore",
      "readinessComponents",
      "readinessHoldingBack",
      "readinessRevenue",
      "readinessTaken",
    ],
  },
};

/**
 * Adds or removes one column. Returns `undefined` when the result is the view's
 * own preset again, so the URL drops back to the plain view.
 */
export function toggleColumn(
  view: CoachDirectoryViewId,
  current: readonly CoachDirectoryColumnId[],
  column: CoachDirectoryColumnId,
): CoachDirectoryColumnId[] | undefined {
  const next = current.includes(column)
    ? current.filter((id) => id !== column)
    : [...current, column];
  const preset = VIEWS[view].columns;
  const matchesPreset =
    next.length === preset.length &&
    next.every((id, index) => id === preset[index]);

  return matchesPreset ? undefined : next;
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/** Identity columns lead every export so a sheet can always be joined back. */
export function buildCoachDirectoryCsv(
  rows: readonly CoachDirectoryRow[],
  columns: readonly CoachDirectoryColumnId[],
  origin: string,
): string {
  const exported = columns.flatMap((id) => {
    const { label, csv } = COLUMNS[id];
    return csv === null ? [] : [{ label, csv }];
  });

  return toCsv(
    [
      "Coach ID",
      "Name",
      "Email",
      "Profile URL",
      ...exported.map((c) => c.label),
    ],
    rows.map((row) => [
      row.id,
      row.fullName,
      row.email,
      `${origin}/${row.username}`,
      ...exported.map((column) => column.csv(row)),
    ]),
  );
}
